import type { RegionNode, RegionTree } from '@map/shared';
import type { Territory } from '@map/worldkit';

/**
 * What the globe draws, and what it labels — one rule, so the two can never
 * disagree.
 *
 * Opening a place used to push the whole world one level deeper, so selecting
 * a country replaced every country on the planet with its cities. You would
 * click Revision and the name under the cursor became Juz 30, which is not
 * where you are. Only the place you opened unfolds now; its neighbours stay at
 * their own level, whole and named, and everything else is untouched.
 */
export function visibleTerritories(
  territories: Map<string, Territory>,
  tree: RegionTree,
  depth: number,
  selectedId: string | null,
): Array<{ region: RegionNode; territory: Territory }> {
  const selected = selectedId ? tree.regions[selectedId] : undefined;

  if (!selected) {
    const out: Array<{ region: RegionNode; territory: Territory }> = [];
    for (const [id, territory] of territories) {
      const region = tree.regions[id];
      if (!region) continue;
      const leaf = territory.level < depth && region.children.length === 0;
      if (territory.level === depth || leaf) out.push({ region, territory });
    }
    return out;
  }

  // Everything on the way down to the open place, so each branch is shown at
  // the shallowest level that is not on that path. Drawing the whole planet at
  // the open place's own depth instead meant that opening a city in Revision
  // also cracked Memorisation open, and the label over Memorisation became the
  // name of a city inside it — a place nobody had opened.
  const spine = new Set<string | null>([null, selected.id]);
  for (
    let node: RegionNode | undefined = selected;
    node?.parentId;
    node = tree.regions[node.parentId]
  ) {
    spine.add(node.parentId);
  }

  const out: Array<{ region: RegionNode; territory: Territory }> = [];
  for (const [id, territory] of territories) {
    const region = tree.regions[id];
    if (!region || spine.has(id)) continue;
    if (spine.has(region.parentId)) out.push({ region, territory });
  }

  // A place with nothing inside it is drawn as itself: there is nothing to
  // unfold into.
  if (selected.children.length === 0) {
    const territory = territories.get(selected.id);
    if (territory) out.push({ region: selected, territory });
  }
  return out;
}
